import {createContext, useContext, useMemo} from "react";
import type {
    ChecklistElements,
    ChecklistMetadata,
    ChecklistObjects,
    ChecklistToc,
    CollectionDataResponse,
    LabelGroup,
    RenderableDocument,
    Status,
} from "../types";

// ---------------------------------------------------------------------------
// Structure context — changes on collection/dimension switch
// ---------------------------------------------------------------------------

export interface ChecklistStructureValue {
    collectionData?: CollectionDataResponse;
    isDraft: boolean;
    viewFilteredToc: ChecklistToc;
    effectiveRootNodeId: string;
    infoIdSet: Set<string>;
    checklistConfig: {
        metadata: ChecklistMetadata;
        viewGroups: LabelGroup[];
        labelData: Record<string, string>;
        defaultCollectionId: string;
    };
}

const ChecklistStructureContext = createContext<
    ChecklistStructureValue | undefined
>(undefined);

export const ChecklistStructureProvider = ChecklistStructureContext.Provider;

// eslint-disable-next-line react-refresh/only-export-components
export const useChecklistStructure = (): ChecklistStructureValue => {
    const ctx = useContext(ChecklistStructureContext);
    if (!ctx)
        throw new Error(
            "useChecklistStructure must be used within ChecklistStructureProvider",
        );
    return ctx;
};

// ---------------------------------------------------------------------------
// Interaction context — changes on search/filter/expand
// ---------------------------------------------------------------------------

export interface ChecklistInteractionValue {
    isHidden: (id: string) => boolean;
    isSearchHit: (id: string) => boolean;
    getStatus: (id: string) => Status | -1;
    itemsState: {
        loadingState: Record<
            string,
            {isLoading: boolean; success: boolean; error: boolean}
        >;
        elements: ChecklistElements;
        objects: ChecklistObjects;
        openItems: Record<string, boolean>;
        fetchingAll: boolean;
    };
    searchTerm: string;
    observeChecklistItemRef: (ref: HTMLDivElement) => void;
    unobserveChecklistItemRef: (ref: HTMLDivElement) => void;
    onClickItem: (id: string, parentId: string) => void;
    onClickTocItem: (id: string, parentId: string) => void;
    onCheckItemStatusClick: (params: {id: string; status: Status}) => void;
    topicData: RenderableDocument;
}

const ChecklistInteractionContext = createContext<
    ChecklistInteractionValue | undefined
>(undefined);

export const ChecklistInteractionProvider =
    ChecklistInteractionContext.Provider;

// eslint-disable-next-line react-refresh/only-export-components
export const useChecklistInteraction = (): ChecklistInteractionValue => {
    const ctx = useContext(ChecklistInteractionContext);
    if (!ctx)
        throw new Error(
            "useChecklistInteraction must be used within ChecklistInteractionProvider",
        );
    return ctx;
};

// ---------------------------------------------------------------------------
// Combined hook — backward-compatible for existing deep-tree consumers
// ---------------------------------------------------------------------------

export type ChecklistDataContextValue = ChecklistStructureValue &
    ChecklistInteractionValue;

// eslint-disable-next-line react-refresh/only-export-components
export const useChecklistData = (): ChecklistDataContextValue => {
    const structure = useChecklistStructure();
    const interaction = useChecklistInteraction();
    return useMemo(
        () => ({...structure, ...interaction}),
        [structure, interaction],
    );
};
